/**
 * Independent client-side rate limits for SIM vs IMEI verify on preloaded SIM device step.
 * Same model as IMEI check landing: each completed verify attempt counts for that field only;
 * after {@link PRELOADED_SIM_DEVICE_VERIFY_MAX_ATTEMPTS} attempts, {@link PRELOADED_SIM_DEVICE_VERIFY_COOLDOWN_MS} cooldown applies.
 */

export const PRELOADED_SIM_DEVICE_VERIFY_MAX_ATTEMPTS = 3;
export const PRELOADED_SIM_DEVICE_VERIFY_COOLDOWN_MS = 120_000;

const STORAGE_KEY = "vle-preloaded-sim-device-verify-attempt-state";

export type PreloadedSimDeviceVerifyFieldKey = "sim" | "imei";

export type PreloadedSimDeviceVerifyFieldState = {
  attempts: number;
  cooldownUntil: number | null;
};

export type PreloadedSimDeviceVerifyStore = {
  sim: PreloadedSimDeviceVerifyFieldState;
  imei: PreloadedSimDeviceVerifyFieldState;
};

const emptyField = (): PreloadedSimDeviceVerifyFieldState => ({
  attempts: 0,
  cooldownUntil: null,
});

function normalizeField(raw: unknown): PreloadedSimDeviceVerifyFieldState {
  if (!raw || typeof raw !== "object") return emptyField();
  const o = raw as Record<string, unknown>;
  const attempts =
    typeof o.attempts === "number" && o.attempts > 0 ? o.attempts : 0;
  const cooldownUntil =
    typeof o.cooldownUntil === "number" ? o.cooldownUntil : null;
  return { attempts, cooldownUntil };
}

function readAll(): PreloadedSimDeviceVerifyStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { sim: emptyField(), imei: emptyField() };
    }
    const parsed = JSON.parse(raw) as Record<string, unknown> & {
      attempts?: unknown;
      cooldownUntil?: unknown;
    };

    if (parsed && typeof parsed === "object") {
      if ("sim" in parsed || "imei" in parsed) {
        return {
          sim: normalizeField(parsed.sim),
          imei: normalizeField(parsed.imei),
        };
      }
      /** Legacy single-blob shape → treat as SIM-only so we do not lose an active cooldown. */
      if ("attempts" in parsed || "cooldownUntil" in parsed) {
        return {
          sim: normalizeField(parsed),
          imei: emptyField(),
        };
      }
    }
    return { sim: emptyField(), imei: emptyField() };
  } catch {
    return { sim: emptyField(), imei: emptyField() };
  }
}

function writeAll(store: PreloadedSimDeviceVerifyStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // quota / private mode — degrade gracefully
  }
}

export function getPreloadedSimDeviceVerifyAttemptStorageKey(): string {
  return STORAGE_KEY;
}

function resolveFieldIfExpired(
  field: PreloadedSimDeviceVerifyFieldKey,
  store: PreloadedSimDeviceVerifyStore,
): PreloadedSimDeviceVerifyStore {
  const s = store[field];
  const now = Date.now();
  if (s.cooldownUntil != null && now >= s.cooldownUntil) {
    const next = {
      ...store,
      [field]: emptyField(),
    };
    writeAll(next);
    return next;
  }
  return store;
}

export function resolvePreloadedSimDeviceVerifyFieldState(
  field: PreloadedSimDeviceVerifyFieldKey,
): PreloadedSimDeviceVerifyFieldState {
  const store = resolveFieldIfExpired(field, readAll());
  return store[field];
}

export function recordPreloadedSimDeviceVerifyFieldAttempt(
  field: PreloadedSimDeviceVerifyFieldKey,
): PreloadedSimDeviceVerifyFieldState {
  let store = readAll();
  store = resolveFieldIfExpired("sim", store);
  store = resolveFieldIfExpired("imei", store);

  const current = store[field];
  const now = Date.now();
  if (current.cooldownUntil != null && now < current.cooldownUntil) {
    return current;
  }

  const attempts = current.attempts + 1;
  const cooldownUntil =
    attempts >= PRELOADED_SIM_DEVICE_VERIFY_MAX_ATTEMPTS
      ? now + PRELOADED_SIM_DEVICE_VERIFY_COOLDOWN_MS
      : null;
  const nextField: PreloadedSimDeviceVerifyFieldState = {
    attempts,
    cooldownUntil,
  };
  const nextStore: PreloadedSimDeviceVerifyStore = {
    ...store,
    [field]: nextField,
  };
  writeAll(nextStore);
  return nextField;
}

export function remainingPreloadedSimDeviceVerifyCooldownSeconds(
  cooldownUntil: number | null,
  nowMs: number = Date.now(),
): number {
  if (cooldownUntil == null || nowMs >= cooldownUntil) return 0;
  return Math.ceil((cooldownUntil - nowMs) / 1000);
}

export function getPreloadedSimDeviceVerifyFieldSnapshot(
  field: PreloadedSimDeviceVerifyFieldKey,
): {
  attempts: number;
  inCooldown: boolean;
  cooldownUntil: number | null;
  attemptsRemaining: number;
} {
  const { attempts, cooldownUntil } =
    resolvePreloadedSimDeviceVerifyFieldState(field);
  const now = Date.now();
  const inCooldown = cooldownUntil != null && now < cooldownUntil;
  const attemptsRemaining = inCooldown
    ? 0
    : Math.max(0, PRELOADED_SIM_DEVICE_VERIFY_MAX_ATTEMPTS - attempts);

  return {
    attempts,
    inCooldown,
    cooldownUntil,
    attemptsRemaining,
  };
}

export function clearPreloadedSimDeviceVerifyAttemptState(): void {
  writeAll({ sim: emptyField(), imei: emptyField() });
}
